import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Building2,
  Wallet,
  Sparkles,
  Inbox as InboxIcon,
  Mail,
  MessageCircle,
  UserCog,
  LogOut,
  Search,
  HardDrive,
  BookOpen,
  Megaphone,
  Settings2,
  Loader2,
  Smartphone,
  ShieldCheck,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ManualSettingsDialog from "@/components/ManualSettingsDialog";

/**
 * Layout permanente per la sezione Super-Admin.
 * Sidebar verticale fissa a sinistra (icone + label, collassabile su mobile)
 * con voci: Agenzie, Pagamenti, Landing & Piani, Inbox, Anagrafica.
 *
 * Renderizza <Outlet/> per le route figlie.
 */
export default function SuperAdminLayout() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [manualBusy, setManualBusy] = useState(false);
  const [brochureBusy, setBrochureBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const downloadPdf = async (endpoint, kind, setLoading) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await fetch(`${API}${endpoint}`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const errTxt = await resp.text().catch(() => "");
        throw new Error(errTxt || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || `${kind}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "Brochure" ? "Brochure" : "Manuale d'uso"} scaricato`);
    } catch (e) {
      toast.error(e?.message || "Errore nel download");
    } finally {
      setLoading(false);
    }
  };

  const downloadUserManual = () =>
    !manualBusy && downloadPdf("/superadmin/user-manual/pdf", "Manuale", setManualBusy);
  const downloadBrochure = () =>
    !brochureBusy && downloadPdf("/superadmin/brochure/pdf", "Brochure", setBrochureBusy);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/profile");
        setProfile(data);
      } catch {
        /* silent */
      }
    })();
    // Fetch contatti non letti (leggero — solo count)
    (async () => {
      try {
        const { data } = await api.get("/superadmin/contact-requests");
        setUnreadContacts(data?.unread || 0);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const navItems = [
    { to: "/superadmin", end: true, label: "Agenzie", icon: Building2, testId: "nav-agencies" },
    { to: "/superadmin/payments", label: "Pagamenti", icon: Wallet, testId: "nav-payments" },
    { to: "/superadmin/landing", label: "Landing & Piani", icon: Sparkles, testId: "nav-landing" },
    { to: "/superadmin/contacts", label: "Contatti", icon: MessageCircle, testId: "nav-contacts", badge: unreadContacts },
    { to: "/superadmin/storage", label: "Storage", icon: HardDrive, testId: "nav-storage" },
    { to: "/superadmin/gdpr", label: "Privacy & GDPR", icon: ShieldCheck, testId: "nav-gdpr" },
    { to: "/superadmin/android-app", label: "App Android", icon: Smartphone, testId: "nav-android-app" },
    { to: "/superadmin/search", label: "Ricerca globale", icon: Search, testId: "nav-search" },
  ];

  const linkBase =
    "flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors";
  const linkActive =
    "bg-white text-indigo-700 shadow-sm";
  const linkIdle =
    "text-white/85 hover:bg-white/10 hover:text-white";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside
        className="hidden md:flex w-60 shrink-0 bg-gradient-to-b from-violet-700 to-indigo-800 text-white flex-col fixed h-screen overflow-y-auto"
        data-testid="superadmin-sidebar"
      >
        <div className="px-4 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-violet-200" />
            <div className="leading-tight">
              <div className="text-sm font-display font-semibold">Gestionale Assicurativo</div>
              <div className="text-[10px] uppercase tracking-wider text-violet-200/80">Super-Admin</div>
            </div>
          </div>
          {profile?.email && (
            <div className="mt-2 text-[10px] text-violet-200/80 truncate" data-testid="sa-sidebar-email">
              {profile.email}
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testId}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{it.label}</span>
                {it.badge > 0 && (
                  <span
                    data-testid={`${it.testId}-badge`}
                    className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                    title={`${it.badge} non letti`}
                  >
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-white/10 space-y-1">
          <button
            data-testid="sa-open-inbox-health"
            onClick={() => nav("/superadmin?openInboxHealth=1")}
            className={`${linkBase} ${linkIdle} w-full text-left`}
          >
            <InboxIcon className="h-4 w-4" />
            <span>🏥 Salute Inbox agenzie</span>
          </button>
          <button
            data-testid="sa-open-inbox-settings"
            onClick={() => nav("/superadmin?openInbox=1")}
            className={`${linkBase} ${linkIdle} w-full text-left`}
          >
            <Mail className="h-4 w-4" />
            <span>📧 Email di Sistema</span>
          </button>
          <button
            data-testid="sa-open-profile"
            onClick={() => nav("/superadmin?openProfile=1")}
            className={`${linkBase} ${linkIdle} w-full text-left`}
          >
            <UserCog className="h-4 w-4" />
            <span>Anagrafica</span>
          </button>
          <button
            data-testid="sa-download-user-manual"
            onClick={downloadUserManual}
            disabled={manualBusy}
            className={`${linkBase} ${linkIdle} w-full text-left disabled:opacity-60 disabled:cursor-wait`}
            title="Scarica la guida completa del gestionale in PDF"
          >
            {manualBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <BookOpen className="h-4 w-4" />
            )}
            <span>📘 Manuale d&apos;Uso</span>
          </button>
          <button
            data-testid="sa-download-brochure"
            onClick={downloadBrochure}
            disabled={brochureBusy}
            className={`${linkBase} ${linkIdle} w-full text-left disabled:opacity-60 disabled:cursor-wait`}
            title="Scarica la brochure commerciale (pubblicità)"
          >
            {brochureBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Megaphone className="h-4 w-4" />
            )}
            <span>📣 Brochure</span>
          </button>
          <button
            data-testid="sa-open-manual-settings"
            onClick={() => setSettingsOpen(true)}
            className={`${linkBase} ${linkIdle} w-full text-left`}
            title="Configura logo, intestazione, contatti dei PDF"
          >
            <Settings2 className="h-4 w-4" />
            <span>Config. Manuali</span>
          </button>
          <button
            data-testid="sa-logout"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className={`${linkBase} ${linkIdle} w-full text-left`}
          >
            <LogOut className="h-4 w-4" />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Mobile top-bar: visibile solo sotto md */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-gradient-to-r from-violet-700 to-indigo-700 text-white shadow-md">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs font-semibold truncate">Super-Admin</span>
          </div>
          <div className="flex gap-1">
            {navItems.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    `p-1.5 rounded ${isActive ? "bg-white/20" : "hover:bg-white/10"}`
                  }
                  title={it.label}
                >
                  <Icon className="h-4 w-4" />
                </NavLink>
              );
            })}
            <button
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              className="p-1.5 rounded hover:bg-white/10"
              title="Esci"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 md:ml-60 pt-12 md:pt-0 min-w-0">
        <Outlet />
      </main>

      <ManualSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
